import { useEffect, useState } from 'react';
import { call } from './api';

/** Tautulli (alleen de beheerder): status, en wat de pagina's laden. */

export interface TautulliStatus { enabled: boolean; inNav: boolean; widget: boolean; userStats: boolean }

export interface TSession {
  key: string; id: string | null; user: string; userThumb: string | null; player: string; platform: string | null;
  title: string; sub: string; mediaType: string | null; poster: string | null; art: string | null;
  progress: number; offset: number; duration: number; state: string;
  decision: 'direct play' | 'direct stream' | 'transcode'; why: string | null; from: string | null; to: string | null; hw: boolean;
  quality: string | null; bandwidth: number; location: string | null; relayed: boolean;
}

export interface TActivity { streams: TSession[]; totals: { streams: number; bandwidth: number; lan: number; wan: number; directPlay: number; directStream: number; transcode: number } }
export interface TGraph { categories: string[]; series: { name: string; data: number[] }[] }
export interface TOverview { days: number; byDate: TGraph; streamTypes: { directPlay: number; directStream: number; transcode: number }; busiestDay: string | null; busiestHour: string | null }
export interface TMedia { title: string; plays: number; duration: number; users: number | null; poster: string | null; ratingKey: string | null; year: number | null }
export interface TPopular { days: number; movies: TMedia[]; shows: TMedia[]; music: TMedia[]; users: { name: string; plays: number; duration: number; thumb: string | null }[]; platforms: { name: string; plays: number; duration: number }[] }
export interface THistoryRow { at: number; user: string; userId: number | null; title: string; mediaType: string | null; poster: string | null; platform: string; decision: string; percent: number; watched: number }
export interface TUser { id: number; name: string; thumb: string | null; plays: number; duration: number; lastSeen: number | null; lastPlayed: string | null; device: string | null }
export interface TLibrary { id: string; name: string; type: string; count: number; parents: number; children: number; plays: number; duration: number; lastAccessed: number | null; lastPlayed: string | null }

export const tautulliApi = {
  status: () => call<TautulliStatus>('/api/tautulli/status'),
  config: () => call<{ configured: boolean; url: string | null; inNav: boolean; widget: boolean; userStats: boolean }>('/api/tautulli/config'),
  save: (b: { url?: string; apiKey?: string; inNav?: boolean; widget?: boolean; userStats?: boolean }) => call<{ ok: true; version: string | null }>('/api/tautulli/config', { method: 'PUT', body: JSON.stringify(b) }),
  toggles: (b: { inNav?: boolean; widget?: boolean; userStats?: boolean }) => call<{ ok: true }>('/api/tautulli/config', { method: 'PATCH', body: JSON.stringify(b) }),
  remove: () => call<{ ok: true }>('/api/tautulli/config', { method: 'DELETE' }),
  test: () => call<{ ok: true; version: string | null; server: string | null; libraries: number; users: number }>('/api/tautulli/test', { method: 'POST' }),
  activity: () => call<TActivity | null>('/api/tautulli/activity'),
  terminate: (key: string, id: string | null, message: string) => call<{ ok: true }>('/api/tautulli/terminate', { method: 'POST', body: JSON.stringify({ key, id, message }) }),
  overview: (days: number) => call<TOverview>(`/api/tautulli/overview?days=${days}`),
  popular: (days: number) => call<TPopular>(`/api/tautulli/popular?days=${days}`),
  history: (start = 0, user?: number) => call<{ total: number; rows: THistoryRow[] }>(`/api/tautulli/history?start=${start}${user ? `&user=${user}` : ''}`),
  users: () => call<{ users: TUser[] }>('/api/tautulli/users'),
  libraries: () => call<{ libraries: TLibrary[] }>('/api/tautulli/libraries'),
  userStats: () => call<{ stats: Record<string, { month: number; total: number; plays: number; lastSeen: number | null }> }>('/api/tautulli/user-stats'),
};

let cached: { uuid: string; p: Promise<TautulliStatus> } | null = null;
const listeners = new Set<() => void>();

export function refreshTautulliStatus() {
  cached = null;
  listeners.forEach((l) => l());
}

export function useTautulliStatus(uuid: string): TautulliStatus | null {
  const [st, setSt] = useState<TautulliStatus | null>(null);
  useEffect(() => {
    const load = () => {
      if (!cached || cached.uuid !== uuid) cached = { uuid, p: tautulliApi.status().catch(() => ({ enabled: false, inNav: false, widget: false, userStats: false })) };
      void cached.p.then(setSt);
    };
    load();
    listeners.add(load);
    return () => {
      listeners.delete(load);
    };
  }, [uuid]);
  return st;
}

export function hours(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return h >= 1 ? `${h} u${m ? ` ${m} m` : ''}` : `${m} m`;
}

export function mbps(kbps: number): string {
  return `${(kbps / 1000).toFixed(1).replace('.', ',')} Mbps`;
}

export function ago(ms: number | null): string {
  if (!ms) return '—';
  const d = Date.now() - ms;
  if (d < 5 * 60_000) return 'nu';
  if (d < 3600_000) return `${Math.round(d / 60_000)} min geleden`;
  if (d < 86400_000) return `${Math.round(d / 3600_000)} uur geleden`;
  if (d < 2 * 86400_000) return 'gisteren';
  return new Date(ms).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

export const DECISION: Record<string, { label: string; cls: string }> = {
  'direct play': { label: 'Direct Play', cls: 'dec-dp' },
  'direct stream': { label: 'Direct Stream', cls: 'dec-ds' },
  transcode: { label: 'Transcode', cls: 'dec-tc' },
};
